<template>
  <div
    class="ai-sidebar"
    :class="{ 'is-open': isOpen }"
    @click="handleOutsideClick"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <!-- Overlay de dépôt (glisser-déposer un fichier dans la discussion) -->
    <div v-if="isDragging" class="moo-drop-overlay">
      <div class="moo-drop-inner">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="17 8 12 3 7 8"></polyline>
          <line x1="12" y1="3" x2="12" y2="15"></line>
        </svg>
        <div class="moo-drop-title">Déposez votre fichier ici</div>
        <div class="moo-drop-sub">Image, PDF, Excel, Word, audio ou vidéo</div>
      </div>
    </div>

    <!-- Barre supérieure Premium Moo Agent -->
    <div class="moo-conv-bar">
      <!-- Partie Gauche : Branding Moo Agent -->
      <div class="moo-brand-header">
        <div class="moo-brand-logo-small">M</div>
        <span class="moo-brand-name">Moo Agent</span>
        <div class="moo-active-dot"></div>
      </div>
      
      <!-- Titre de la conversation courante plus discret -->
      <div class="moo-conv-title-subtle" :title="currentConversationTitle">
        {{ currentConversationTitle }}
      </div>

      <!-- Partie Droite : Actions de la conversation -->
      <div class="moo-header-actions">
        <!-- Bouton Constitution -->
        <button class="moo-conv-icon-btn" @click.stop="$emit('open-constitution')" title="Constitution de l'agent">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
          </svg>
        </button>

        <!-- Bouton Nouveau (+) -->
        <button class="moo-conv-icon-btn" @click.stop="newConversation" title="Nouvelle conversation">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>

        <!-- Bouton Historique (Horloge) -->
        <button class="moo-conv-icon-btn" @click.stop="showConvPanel = !showConvPanel" title="Historique des conversations">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <polyline points="3 3 3 8 8 8"/>
            <line x1="12" y1="7" x2="12" y2="12"/>
            <line x1="12" y1="12" x2="16" y2="14"/>
          </svg>
        </button>

        <!-- Bouton Récap variables (crée/MAJ un bloc de test listant toutes les variables) -->
        <button class="moo-conv-icon-btn" @click.stop="$emit('create-recap-block')" title="Créer/MAJ le bloc « 🔍 Récap variables » (test)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
            <line x1="11" y1="8" x2="11" y2="14"></line>
          </svg>
        </button>

        <!-- Bouton Fermer (X) -->
        <button class="moo-conv-icon-btn close-sidebar-btn" @click.stop="$emit('close')" title="Fermer la sidebar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- Panneau latéral des conversations -->
    <div v-if="showConvPanel" class="moo-conv-panel-overlay" @click.self="showConvPanel = false">
      <div class="moo-conv-panel" @click.stop>
        <div class="moo-conv-panel-header">
          <span class="moo-conv-panel-title">Mes conversations</span>
          <button class="moo-conv-panel-close" @click="showConvPanel = false">×</button>
        </div>
        <button class="moo-conv-new-btn" @click="newConversation">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>Nouvelle conversation</span>
        </button>
        <!-- Recherche de conversations -->
        <div class="moo-conv-search-box">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            v-model="convSearchQuery"
            placeholder="Rechercher une conversation..."
            @click.stop
          />
          <button
            v-if="convSearchQuery"
            class="moo-conv-search-clear"
            @click.stop="convSearchQuery = ''"
            title="Effacer la recherche"
          >
            ×
          </button>
        </div>

        <div class="moo-conv-list">
          <div v-if="conversationsLoading" class="moo-conv-empty">Chargement…</div>
          <div v-else-if="filteredAndGroupedConversations.length === 0" class="moo-conv-empty">
            {{ convSearchQuery ? 'Aucun résultat trouvé' : 'Aucune conversation pour l\'instant.' }}
          </div>
          <template v-else v-for="group in filteredAndGroupedConversations" :key="group.key">
            <div class="moo-conv-group-title">{{ group.label }}</div>
            <div
              v-for="conv in group.items"
              :key="conv.idconversation"
              class="moo-conv-item"
              :class="{ 'is-active': conv.idconversation === idconversation }"
              @click="loadConversation(conv.idconversation)"
            >
              <div class="moo-conv-item-main">
                <div v-if="renamingConvId === conv.idconversation" class="moo-conv-item-rename">
                  <input
                    type="text"
                    v-model="renamingTitle"
                    @click.stop
                    @keydown.enter="confirmRename(conv)"
                    @keydown.esc="cancelRename"
                    ref="renameInput"
                    maxlength="60"
                  />
                </div>
                <div v-else class="moo-conv-item-title">{{ conv.title || 'Sans titre' }}</div>
                <div class="moo-conv-item-meta">{{ formatConvDate(conv.updated_at) }} · {{ conv.message_count || 0 }} msg</div>
              </div>
              <div class="moo-conv-item-actions" @click.stop>
                <button v-if="renamingConvId !== conv.idconversation" class="moo-conv-item-action" @click="startRename(conv)" title="Renommer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                </button>
                <button v-else class="moo-conv-item-action ok" @click="confirmRename(conv)" title="Valider">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </button>
                <button class="moo-conv-item-action danger" @click="deleteConversation(conv)" title="Supprimer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6M14 11v6"></path></svg>
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="ai-chat-area" ref="chatArea">
      <div v-if="messages.length === 0" class="empty-chat">
        <div class="empty-glow"></div>
        <div class="empty-content">
          <div class="moo-brand-logo">M</div>
          <h2 class="moo-welcome">Bonjour, moi c'est Moo 👋</h2>
          <p class="moo-question">Je construis ton assistant IA pour toi. Dis-moi simplement, en langage courant, ce que ton assistant doit faire — et je m'occupe du reste.</p>

          <div class="suggestions-grid">
            <div class="suggestion-card" @click="inputPrompt = 'Crée un agent qui accueille mes clients et répond à leurs questions'; sendMessage()">
              <span class="sugg-icon">👋</span>
              <span>Accueillir mes clients</span>
            </div>
            <div class="suggestion-card" @click="inputPrompt = 'Crée un agent qui répond aux questions fréquentes de mes clients'; sendMessage()">
              <span class="sugg-icon">💬</span>
              <span>Répondre aux questions fréquentes</span>
            </div>
            <div class="suggestion-card" @click="inputPrompt = 'Crée un agent qui prend les commandes de ma boutique'; sendMessage()">
              <span class="sugg-icon">🛍️</span>
              <span>Prendre des commandes</span>
            </div>
            <div class="suggestion-card" @click="inputPrompt = 'Crée un agent qui collecte les coordonnees de mes prospects'; sendMessage()">
              <span class="sugg-icon">📇</span>
              <span>Collecter des prospects</span>
            </div>
          </div>

          <div class="hint-pill">Écris à Moo comme à un collègue — il te pose des questions si besoin</div>
        </div>
      </div>

      <div
        v-for="(msg, index) in messages"
        :key="index"
        class="message-wrapper"
        :class="msg.role === 'user' ? 'user-msg' : 'ai-msg'"
      >
        <div class="bubble-stack" :class="msg.role === 'user' ? 'bubble-stack-end' : 'bubble-stack-start'">
        <div v-if="msg.role === 'user' && msg.snapshot" class="undo-btn-row">
          <button class="undo-icon-btn" @click="undoAction(index)" title="Annuler et modifier">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 7v6h6"/>
              <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
            </svg>
          </button>
        </div>
        <div class="message-bubble" :class="{ 'injected-bubble': msg.role === 'user' && msg.injected }">
          <!-- Prompt injecté automatiquement (continuation / clic sur bouton de plan) :
               affiché replié et grisé, comme une "réflexion" système, réouvrable au clic. -->
          <div v-if="msg.role === 'user' && msg.injected" class="injected-prompt">
            <button type="button" class="injected-toggle" @click="msg.injectedOpen = !msg.injectedOpen">
              <svg class="injected-spark" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"/>
              </svg>
              <span class="injected-label">instruction automatique</span>
              <span class="injected-see">{{ msg.injectedOpen ? 'masquer' : 'voir' }}</span>
              <svg class="injected-chevron" :class="{ open: msg.injectedOpen }" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div v-if="msg.injectedOpen" class="injected-body">{{ msg.content }}</div>
          </div>
          <div v-else class="message-content">
            <div class="msg-text-wrapper">
              <!-- Dropdown Réflexion (extended thinking) -->
              <div v-if="msg.role === 'ai' && msg.reasoning" class="msg-reasoning">
                <button class="msg-reasoning-toggle" @click="msg.reasoningOpen = !msg.reasoningOpen">
                  <svg class="reasoning-spark" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"/>
                  </svg>
                  <span>Réflexion</span>
                  <svg class="reasoning-chevron" :class="{ open: msg.reasoningOpen }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                <div v-if="msg.reasoningOpen" class="msg-reasoning-body">{{ msg.reasoning }}</div>
              </div>

              <!-- Fichier importé (affiché dans la discussion) -->
              <div v-if="msg.file" class="msg-file-block">
                <a
                  v-if="isImageFile(msg.file) && msg.file.url"
                  :href="msg.file.url"
                  target="_blank"
                  rel="noopener"
                  class="msg-file-thumb"
                >
                  <img :src="msg.file.url" :alt="msg.file.name" />
                </a>
                <div class="msg-file-chip" :title="msg.file.name">
                  <span class="file-emoji">{{ fileKindIcon(msg.file.name, msg.file.kind) }}</span>
                  <a
                    v-if="msg.file.url"
                    :href="msg.file.url"
                    target="_blank"
                    rel="noopener"
                    class="msg-file-name msg-file-link"
                  >{{ msg.file.name }}</a>
                  <span v-else class="msg-file-name">{{ msg.file.name }}</span>
                  <span v-if="msg.file.truncated" class="msg-file-trunc">tronqué</span>
                </div>
              </div>

              <span
                v-if="msg.content"
                class="msg-text"
                v-html="renderRich(msg.content)"
                @click="onRichClick"
              ></span>

              <!-- Boutons de clarification / plan -->
              <div v-if="msg.options && msg.options.length > 0 && !msg.optionsUsed" class="msg-options-row">
                <button
                  v-for="(opt, oi) in msg.options"
                  :key="oi"
                  class="msg-option-btn"
                  :class="oi === 0 ? 'opt-primary' : 'opt-secondary'"
                  @click="sendQuickReply(opt.value, msg)"
                  :disabled="isProcessing"
                >
                  {{ opt.label }}
                </button>
              </div>

              <div v-if="msg.warnings && msg.warnings.length > 0" class="msg-warnings-box">
                <div class="msg-warnings-header">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  Remarque
                </div>
                <ul>
                  <li v-for="(warn, i) in msg.warnings" :key="i">{{ warn }}</li>
                </ul>
                <div class="msg-warnings-footer">Souhaitez-vous modifier autre chose ?</div>
              </div>

              <!-- Plan incomplet : bouton Continuer -->
              <div v-if="msg.incompletePlan && !msg.continueUsed" class="msg-options-row">
                <button
                  class="msg-option-btn opt-primary"
                  @click="continuePlan(msg)"
                  :disabled="isProcessing"
                >
                  ▶︎ Continuer le plan
                </button>
              </div>

              <!-- Bouton Tester le flow -->
              <div v-if="msg.showTestButton" class="msg-options-row" style="margin-top: 8px;">
                <button
                  class="msg-option-btn opt-primary"
                  @click="$emit('test-flow')"
                  title="Ouvrir la bulle de test"
                  style="display: flex; align-items: center; gap: 6px;"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  Tester le flow
                </button>
              </div>

              <!-- Auto-test : corrections proposées par Moo (à confirmer) -->
              <div v-if="msg.selfTestFixes && msg.selfTestFixes.length && !msg.selfTestApplied" class="msg-options-row" style="margin-top: 8px;">
                <button
                  class="msg-option-btn opt-primary"
                  @click="applySelfTestFixes(msg)"
                  :disabled="msg.selfTestApplying || isProcessing"
                  title="Appliquer les corrections proposées par Moo"
                >
                  {{ msg.selfTestApplying ? 'Application…' : '✔ Appliquer les ' + msg.selfTestFixes.length + ' correction(s)' }}
                </button>
              </div>
            </div>
            
            <button v-if="msg.role === 'user'" class="edit-msg-btn" @click="editMessage(msg.content)" title="Modifier">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2.5 2v6h6M2.66 15.57a10 10 0 1 0 .57-8.38l-3.1 3.1"></path>
              </svg>
            </button>
            <div v-if="msg.role === 'ai' && msg.usage" class="msg-token-count">
              <span class="tok-in">↑ {{ formatTokens(Math.max(0, msg.usage.input_tokens - systemPromptTokens)) }}</span>
              <span class="tok-sep">·</span>
              <span class="tok-out">↓ {{ formatTokens(msg.usage.output_tokens) }}</span>
            </div>
            <div v-if="msg.status" class="msg-status" :class="msg.status">
              <div class="bounce-loader" v-if="msg.status === 'loading'">
                <div class="bounce-ball"></div>
              </div>
              <span class="status-text" :class="{'claude-shimmer': msg.status === 'loading'}">{{ msg.statusText }}</span>
              <button v-if="msg.isExecuting" @click="stopExecution" class="stop-execution-btn" title="Arrêter l'exécution">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                Arrêter
              </button>
            </div>
          </div>
        </div>
        </div><!-- /bubble-stack -->
      </div>
    </div>

    <!-- Token footer -->
    <div class="token-footer" v-if="sessionTokens.input > 0">
      <span class="token-item">Session ↑{{ formatTokens(sessionTokens.input) }} ↓{{ formatTokens(sessionTokens.output) }}</span>
    </div>

    <div class="ai-input-area">
      <!-- Mention Menu Popup -->
      <div v-if="showMentionMenu" class="mention-menu">
        <div class="mention-search-bar">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <span>{{ mentionSearchQuery || 'Blocs & variables...' }}</span>
        </div>
        <div class="mention-list">
          <!-- Section Blocs -->
          <template v-if="filteredBlocks.length > 0">
            <div class="mention-section-title">Blocs</div>
            <div
              v-for="(item, index) in filteredBlocks"
              :key="'b-' + item._id"
              class="mention-item"
              :class="{ 'is-selected': mentionIndexOf(item) === selectedMentionIndex }"
              @click="selectMention(item)"
              @mouseenter="selectedMentionIndex = mentionIndexOf(item)"
            >
              <div class="mention-item-icon block-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>
              </div>
              <div class="mention-details">
                <span class="mention-name">{{ item._displayName }}</span>
                <span class="mention-sub">{{ item.idblock }}</span>
              </div>
              <span class="mention-badge block">Bloc</span>
            </div>
          </template>

          <!-- Section Variables -->
          <template v-if="filteredVariables.length > 0">
            <div class="mention-section-title var">Variables</div>
            <div
              v-for="(item, index) in filteredVariables"
              :key="'v-' + item._id"
              class="mention-item"
              :class="{ 'is-selected': mentionIndexOf(item) === selectedMentionIndex }"
              @click="selectMention(item)"
              @mouseenter="selectedMentionIndex = mentionIndexOf(item)"
            >
              <div class="mention-item-icon var-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h16M4 12h16M4 17h7"></path></svg>
              </div>
              <div class="mention-details">
                <span class="mention-name">{{ item._displayName }}</span>
                <span class="mention-sub">{{ item.type || 'text' }}</span>
              </div>
              <span class="mention-badge variable">Var</span>
            </div>
          </template>

          <div v-if="filteredBlocks.length === 0 && filteredVariables.length === 0" class="mention-empty">
            Aucun résultat
          </div>
        </div>
      </div>

      <!-- Chip élément sélectionné -->
      <div v-if="selectedElement" class="selected-element-chip">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
        </svg>
        <span class="chip-label">{{ selectedElementLabel }}</span>
        <button class="chip-clear" @click="$emit('deselect-element')" title="Désélectionner">×</button>
      </div>

      <!-- Chip fichier joint (en attente d'envoi) -->
      <div v-if="attachedFile" class="attached-file-block">
        <img v-if="attachedFilePreviewUrl" :src="attachedFilePreviewUrl" class="attached-file-preview" :alt="attachedFile.name" />
        <div class="attached-file-chip">
          <span class="file-emoji">{{ fileKindIcon(attachedFile.name) }}</span>
          <span class="chip-label">{{ attachedFile.name }}</span>
          <button class="chip-clear" @click="removeAttachedFile" title="Retirer le fichier">×</button>
        </div>
      </div>

      <div class="input-wrapper modern-chat-input">
        <textarea
          v-model="inputPrompt"
          placeholder="Écris ici ce que ton assistant doit faire…"
          @input="handleInput"
          @keydown="handleKeydown"
          rows="1"
          ref="promptInput"
        ></textarea>
        
        <div class="input-actions-row">
          <div class="actions-left" style="position: relative;">
            <button class="icon-btn plus-btn" title="Ajouter" @click.stop="togglePlusMenu">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            </button>
            
            <!-- Plus Menu Popup -->
            <div v-if="showPlusMenu" class="plus-menu" @click.stop>
              <div class="plus-menu-item" @click="openMentionsFromPlus">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"></circle><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"></path></svg>
                <span>Mentions</span>
              </div>
              <div class="plus-menu-item" @click="triggerFileSelect">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
                <span>Importer un fichier</span>
              </div>
            </div>

            <!-- Input fichier caché (documents + images / audio / vidéo) -->
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.txt,.md,image/*,audio/*,video/*,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              style="display: none;"
              @change="onFileSelected"
            />

            <span class="model-badge">Moo Agent</span>
          </div>
          <div class="actions-right">
            <button class="modern-send-btn" @click="sendMessage" :disabled="(!inputPrompt.trim() && !attachedFile) || isProcessing">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosMoo } from '@/utils/axios';

export default {
  name: 'AiCopilotSidebar',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    chatbotId: {
      type: String,
      required: true
    },
    idbot: {
      type: String,
      default: null
    },
    currentBlocks: {
      type: Array,
      default: () => []
    },
    currentVariables: {
      type: Array,
      default: () => []
    },
    currentBlockFlow: {
      type: Object,
      default: null
    },
    selectedElement: {
      type: Object,
      default: null
    },
    constitution: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      inputPrompt: '',
      attachedFile: null,
      attachedFilePreviewUrl: null,
      isDragging: false,
      messages: [],
      isProcessing: false,
      // Mention logic
      showMentionMenu: false,
      mentionSearchQuery: '',
      mentionCursorPosition: 0,
      selectedMentionIndex: 0,
      showPlusMenu: false,
      executionCancelled: false,
      systemPromptTokens: 0,
      sessionTokens: { input: 0, output: 0 },
      // Historique des conversations
      idconversation: null,
      conversations: [],
      conversationsLoading: false,
      showConvPanel: false,
      renamingConvId: null,
      renamingTitle: '',
      convSearchQuery: ''
    }
  },
  mounted() {
    const userData = localStorage.getItem('user');
    const token = userData ? JSON.parse(userData).data.token : '';
    if (token) {
      axiosMoo.get('/ai/system-prompt-tokens', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => { if (r.data?.tokens) this.systemPromptTokens = r.data.tokens; })
        .catch(() => {});
    }
    if (this.idbot) {
      this.loadConversations({ autoOpen: true });
    }
  },
  computed: {
    filteredBlocks() {
      const query = (this.mentionSearchQuery || '').toLowerCase();
      return (this.currentBlocks || [])
        .filter(b => b && (b.idblock || b._id))
        .map(b => ({
          ...b,
          _mentionType: 'block',
          _displayName: b.displayName || b.blockname || b.idblock || 'Bloc',
          _id: b.idblock || b._id
        }))
        .filter(item => !query || item._displayName.toLowerCase().includes(query))
        .slice(0, 8);
    },
    filteredVariables() {
      const query = (this.mentionSearchQuery || '').toLowerCase();
      return (this.currentVariables || [])
        .filter(v => v && v.slug)
        .map(v => ({
          ...v,
          _mentionType: 'variable',
          _displayName: v.slug,
          _id: v.slug
        }))
        .filter(item => !query || item._displayName.toLowerCase().includes(query))
        .slice(0, 6);
    },
    filteredMentions() {
      return [...this.filteredBlocks, ...this.filteredVariables];
    },
    selectedElementLabel() {
      const el = this.selectedElement;
      if (!el) return '';
      const typeLabels = {
        text: 'Texte', text_button: 'Boutons', answer: 'Réponse',
        redirect: 'Redirection', condition: 'Condition', list: 'Liste',
        image: 'Image', delay: 'Délai', variable_insert: 'Variable',
        ask_payment: 'Paiement', notification: 'Notification',
        button_link: 'Lien bouton', request: 'Requête', agent_handoff: 'Agent',
        action: 'Action'
      };
      const label = typeLabels[el.type] || el.type;
      const preview = el.message
        ? ` — "${el.message.slice(0, 30)}${el.message.length > 30 ? '…' : ''}"`
        : el.redirection_block ? ` → ${el.redirection_block}` : '';
      return `${label}${preview}`;
    },
    currentConversationTitle() {
      if (!this.idconversation) return 'Nouvelle conversation';
      const active = this.conversations.find(c => c.idconversation === this.idconversation);
      return (active && active.title) || 'Conversation';
    },
    filteredAndGroupedConversations() {
      const query = (this.convSearchQuery || '').toLowerCase().trim();
      const filtered = this.conversations.filter(c => {
        const title = (c.title || 'Sans titre').toLowerCase();
        return !query || title.includes(query);
      });

      // Grouping by date
      const groups = {
        today: { label: "Aujourd'hui", items: [] },
        yesterday: { label: "Hier", items: [] },
        thisWeek: { label: "Cette semaine", items: [] },
        older: { label: "Plus ancien", items: [] }
      };

      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfYesterday = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
      const startOfWeek = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);

      filtered.forEach(conv => {
        const date = conv.updated_at ? new Date(conv.updated_at) : new Date();
        if (date >= startOfToday) {
          groups.today.items.push(conv);
        } else if (date >= startOfYesterday) {
          groups.yesterday.items.push(conv);
        } else if (date >= startOfWeek) {
          groups.thisWeek.items.push(conv);
        } else {
          groups.older.items.push(conv);
        }
      });

      return Object.entries(groups)
        .map(([key, group]) => ({ key, ...group }))
        .filter(g => g.items.length > 0);
    }
  },
  watch: {
    messages: {
      deep: true,
      handler() {
        this.scrollToBottom();
      }
    },
    isOpen(newVal) {
      if (newVal) {
        setTimeout(() => {
          if (this.$refs.promptInput) this.$refs.promptInput.focus();
        }, 300);
        if (this.idbot) this.loadConversations({ autoOpen: !this.idconversation });
      } else {
        this.closeMentionMenu();
        this.showConvPanel = false;
        this.convSearchQuery = '';
      }
    },
    idbot(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.idconversation = null;
        this.messages = [];
        this.loadConversations({ autoOpen: true });
      }
    }
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatArea;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    handleInput(e) {
      const cursorPosition = e.target.selectionStart;
      const textBeforeCursor = this.inputPrompt.substring(0, cursorPosition);
      
      // Match text starting with '@' up to the cursor, containing no spaces
      const match = textBeforeCursor.match(/@([a-zA-Z0-9_-]*)$/);
      
      if (match) {
        this.showMentionMenu = true;
        this.mentionSearchQuery = match[1];
        this.mentionCursorPosition = cursorPosition - match[0].length;
        this.selectedMentionIndex = 0;
      } else {
        this.closeMentionMenu();
      }

      // Auto resize textarea
      e.target.style.height = 'auto';
      e.target.style.height = (e.target.scrollHeight) + 'px';
    },
    editMessage(content) {
      this.inputPrompt = content;
      this.$nextTick(() => {
        this.$refs.promptInput.focus();
      });
    },
    handleKeydown(e) {
      if (this.showMentionMenu) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (this.selectedMentionIndex < this.filteredMentions.length - 1) {
            this.selectedMentionIndex++;
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (this.selectedMentionIndex > 0) {
            this.selectedMentionIndex--;
          }
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (this.filteredMentions.length > 0) {
            this.selectMention(this.filteredMentions[this.selectedMentionIndex]);
          }
        } else if (e.key === 'Escape') {
          this.closeMentionMenu();
        }
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    selectMention(item) {
      const textBefore = this.inputPrompt.substring(0, this.mentionCursorPosition);
      const cursorPosition = this.$refs.promptInput.selectionStart;
      const textAfter = this.inputPrompt.substring(cursorPosition);
      
      const insertText = `@${item._displayName} `;
      this.inputPrompt = textBefore + insertText + textAfter;
      
      this.closeMentionMenu();
      
      // Reset focus and position
      this.$nextTick(() => {
        const newCursorPos = this.mentionCursorPosition + insertText.length;
        this.$refs.promptInput.focus();
        this.$refs.promptInput.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    mentionIndexOf(item) {
      // Returns the global index in the combined filteredMentions list
      return this.filteredMentions.findIndex(m => m._id === item._id && m._mentionType === item._mentionType);
    },
    closeMentionMenu() {
      this.showMentionMenu = false;
      this.mentionSearchQuery = '';
      this.selectedMentionIndex = 0;
    },
    handleOutsideClick() {
      this.closeMentionMenu();
      this.showPlusMenu = false;
    },
    togglePlusMenu() {
      this.showPlusMenu = !this.showPlusMenu;
      if (this.showPlusMenu) this.closeMentionMenu();
    },
    triggerFileSelect() {
      this.showPlusMenu = false;
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onFileSelected(e) {
      const file = e.target && e.target.files && e.target.files[0];
      if (file) this.setAttachedFile(file);
      // reset pour pouvoir re-sélectionner le même fichier ensuite
      if (e.target) e.target.value = '';
    },
    // Source unique d'attache d'un fichier (input caché OU glisser-déposer)
    setAttachedFile(file) {
      if (!file) return;
      this.attachedFile = file;
      this.clearAttachedPreview();
      // Aperçu local immédiat pour les images (avant même l'upload)
      if (this.fileKindFromName(file.name) === 'image') {
        this.attachedFilePreviewUrl = URL.createObjectURL(file);
      }
      this.showPlusMenu = false;
      this.$nextTick(() => {
        if (this.$refs.promptInput) this.$refs.promptInput.focus();
      });
    },
    // --- Glisser-déposer d'un fichier directement dans la discussion ---
    dragHasFiles(e) {
      const dt = e.dataTransfer;
      return !!(dt && dt.types && Array.from(dt.types).includes('Files'));
    },
    onDragEnter(e) {
      if (!this.dragHasFiles(e)) return;
      e.preventDefault();
      // Compteur : dragenter/dragleave se déclenchent aussi sur les enfants,
      // on n'éteint l'overlay que lorsqu'on a vraiment quitté la sidebar.
      this._dragDepth = (this._dragDepth || 0) + 1;
      this.isDragging = true;
    },
    onDragOver(e) {
      if (!this.dragHasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave(e) {
      if (!this.dragHasFiles(e)) return;
      this._dragDepth = Math.max(0, (this._dragDepth || 0) - 1);
      if (this._dragDepth === 0) this.isDragging = false;
    },
    onDrop(e) {
      this._dragDepth = 0;
      this.isDragging = false;
      if (!this.dragHasFiles(e)) return;
      e.preventDefault();
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.setAttachedFile(file);
    },
    removeAttachedFile() {
      this.attachedFile = null;
      this.clearAttachedPreview();
    },
    clearAttachedPreview() {
      if (this.attachedFilePreviewUrl) {
        try { URL.revokeObjectURL(this.attachedFilePreviewUrl); } catch (e) { /* noop */ }
        this.attachedFilePreviewUrl = null;
      }
    },
    fileKindFromName(name = '') {
      const ext = String(name).toLowerCase().split('.').pop();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
      if (['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'opus'].includes(ext)) return 'audio';
      if (['mp4', 'mov', 'webm', 'mkv', 'avi', '3gp'].includes(ext)) return 'video';
      if (ext === 'pdf') return 'pdf';
      if (['xlsx', 'xls', 'csv'].includes(ext)) return 'excel';
      if (['docx', 'doc'].includes(ext)) return 'word';
      return 'fichier';
    },
    fileKindIcon(name = '', kind = '') {
      const k = kind || this.fileKindFromName(name);
      if (k === 'image') return '🖼️';
      if (k === 'audio') return '🎵';
      if (k === 'video') return '🎬';
      if (k === 'pdf') return '📄';
      if (k === 'excel') return '📊';
      if (k === 'word') return '📝';
      return '📎';
    },
    isImageFile(file) {
      if (!file) return false;
      const k = file.kind || file.elementType || this.fileKindFromName(file.name || '');
      return k === 'image';
    },
    openMentionsFromPlus() {
      this.showPlusMenu = false;
      
      // Ensure space before @ if needed
      if (!this.inputPrompt.endsWith(' ') && this.inputPrompt.length > 0) {
        this.inputPrompt += ' ';
      }
      this.inputPrompt += '@';
      
      this.$nextTick(() => {
        this.$refs.promptInput.focus();
        const cursorPosition = this.inputPrompt.length;
        this.$refs.promptInput.setSelectionRange(cursorPosition, cursorPosition);
        
        // Trigger mention menu explicitly
        this.showMentionMenu = true;
        this.mentionSearchQuery = '';
        this.mentionCursorPosition = cursorPosition - 1;
        this.selectedMentionIndex = 0;
      });
    },
    stopExecution() {
      this.executionCancelled = true;
    },
    sendQuickReply(value, sourceMsg) {
      if (sourceMsg) sourceMsg.optionsUsed = true;
      // Clic sur un bouton de plan = on passe en EXÉCUTION -> couper la réflexion (plus rapide)
      this._executePhase = true;
      // Valeur longue = instruction auto-injectée -> affichée repliée/grisée (pas un message tapé).
      this._injectedPrompt = (typeof value === 'string' && value.length > 80);
      this.inputPrompt = value;
      this.sendMessage();
    },
    continuePlan(sourceMsg) {
      if (sourceMsg) sourceMsg.continueUsed = true;
      // Continuation d'un plan = exécution -> réflexion coupée
      this._executePhase = true;
      const blocks = (sourceMsg && sourceMsg.createdBlocksList) || [];
      if (blocks.length > 0) {
        const blocksList = blocks.map(b => `"${b}"`).join(', ');
        this.inputPrompt = `Continue le plan : les blocs ${blocksList} existent déjà mais sont encore VIDES. Insère maintenant TOUS leurs éléments selon le plan que tu as présenté, bloc par bloc. N'utilise PAS create_block (les blocs existent déjà), utilise UNIQUEMENT insert_element. Ne t'arrête pas avant d'avoir rempli chacun de ces blocs.`;
      } else {
        // Cas plan_incomplete backend : on ne connaît pas les blocs manquants
        // (peut inclure des blocs du plan jamais créés) — relance générique
        this.inputPrompt = `Continue le plan exactement là où il s'est arrêté : compare les blocs existants avec le plan que tu as présenté, puis émets TOUTES les actions restantes (create_block des blocs manquants compris, chacun immédiatement suivi de ses insert_element). Ne ré-émets PAS les actions déjà exécutées. Termine par respond_to_user quand le plan est entièrement couvert.`;
      }
      // Continuation = instruction auto-injectée -> affichée repliée/grisée.
      this._injectedPrompt = true;
      this.sendMessage();
    },
    parseContent(content) {
      if (!content) return [{ type: 'text', value: '' }];
      const regex = /@\[([^\]]+)\]/g;
      const segments = [];
      let lastIndex = 0;
      let match;
      while ((match = regex.exec(content)) !== null) {
        if (match.index > lastIndex) {
          segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
        }
        segments.push({ type: 'block', value: match[1] });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < content.length) {
        segments.push({ type: 'text', value: content.slice(lastIndex) });
      }
      return segments.length ? segments : [{ type: 'text', value: content }];
    },
    openBlockByName(blockName) {
      this.$emit('open-block', blockName);
    },
    // Rendu Markdown SÛR (HTML échappé d'abord -> pas d'injection) et soigné :
    // titres, listes à puces / numérotées, tableaux, séparateurs, gras, code,
    // et mentions @[Bloc] cliquables.
    renderRich(content) {
      if (!content) return '';
      const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      // Formatage "inline" appliqué sur du texte déjà échappé
      const inline = (s) => s
        .replace(/@\[([^\]]+)\]/g, (m, name) => `<span class="msg-block-tag" data-block="${name.replace(/"/g, '&quot;')}">@${name}</span>`)
        .replace(/`([^`\n]+)`/g, '<code class="md-code">$1</code>')
        .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*{1,3}/g, ''); // étoiles résiduelles

      const lines = esc(content).split('\n');
      const out = [];
      let listType = null; // 'ul' | 'ol' | null
      const closeList = () => { if (listType) { out.push(`</${listType}>`); listType = null; } };
      const isTableSep = (s) => /^\|?[\s:|-]+\|?$/.test(s) && s.includes('-');

      let i = 0;
      while (i < lines.length) {
        const line = lines[i];
        const t = line.trim();

        // Séparateur horizontal (--- *** ___ ou —)
        if (/^([-*_]\s*){3,}$/.test(t) || /^—{3,}$/.test(t)) { closeList(); out.push('<hr class="md-hr">'); i++; continue; }

        // Tableau : ligne |..| suivie d'une ligne séparatrice |---|
        if (/^\|.*\|$/.test(t) && i + 1 < lines.length && isTableSep(lines[i + 1].trim())) {
          closeList();
          const cells = (row) => row.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
          const header = cells(t);
          i += 2;
          let tbl = '<table class="md-table"><thead><tr>' + header.map(h => `<th>${inline(h)}</th>`).join('') + '</tr></thead><tbody>';
          while (i < lines.length && /^\|.*\|$/.test(lines[i].trim())) {
            tbl += '<tr>' + cells(lines[i]).map(c => `<td>${inline(c)}</td>`).join('') + '</tr>';
            i++;
          }
          out.push(tbl + '</tbody></table>');
          continue;
        }

        // Titre #..
        const h = t.match(/^#{1,6}\s+(.+?)\s*$/);
        if (h) { closeList(); out.push(`<div class="md-h">${inline(h[1])}</div>`); i++; continue; }

        // Puce - / * / •
        const b = t.match(/^[•\-*]\s+(.+)$/);
        if (b) {
          if (listType !== 'ul') { closeList(); out.push('<ul class="md-ul">'); listType = 'ul'; }
          out.push(`<li>${inline(b[1])}</li>`); i++; continue;
        }
        // Liste numérotée 1. 2. ...
        const o = t.match(/^\d+[.)]\s+(.+)$/);
        if (o) {
          if (listType !== 'ol') { closeList(); out.push('<ol class="md-ol">'); listType = 'ol'; }
          out.push(`<li>${inline(o[1])}</li>`); i++; continue;
        }

        // Ligne vide -> petit espace
        if (t === '') { closeList(); out.push('<div class="md-sp"></div>'); i++; continue; }

        // Paragraphe
        closeList();
        out.push(`<div class="md-p">${inline(t)}</div>`);
        i++;
      }
      closeList();
      return out.join('');
    },
    // Clic délégué sur le rendu : ouvre le bloc si on clique une mention @[Bloc]
    onRichClick(e) {
      const el = e.target && e.target.closest ? e.target.closest('.msg-block-tag') : null;
      if (el && el.dataset && el.dataset.block) {
        this.openBlockByName(el.dataset.block);
      }
    },
    undoAction(msgIndex) {
      const msg = this.messages[msgIndex];
      console.log('%c[MooAI UNDO] undoAction', 'color:#DAFFD1;font-weight:bold', 'index:', msgIndex, 'snapshot:', msg?.snapshot);
      if (!msg || !msg.snapshot) { console.warn('[MooAI UNDO] pas de snapshot sur ce message'); return; }
      this.inputPrompt = msg.content;
      this.$emit('restore-snapshot', msg.snapshot);
      this.messages.splice(msgIndex, 2);
      this.$nextTick(() => {
        if (this.$refs.promptInput) {
          this.$refs.promptInput.focus();
          this.$refs.promptInput.style.height = 'auto';
          this.$refs.promptInput.style.height = this.$refs.promptInput.scrollHeight + 'px';
        }
      });
    },
    formatTokens(n) {
      if (!n) return '0';
      if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
      return String(n);
    },
    buildActionSummary(commands, results) {
      const nameOf = c => c.function || c.name;
      // Avec les résultats d'exécution, ne compter que les actions réellement
      // abouties — sinon le résumé annonce des suppressions/créations jamais faites
      const executed = results && Array.isArray(results.done) ? results.done : commands;
      const failedCount = results && Array.isArray(results.failed) ? results.failed.length : 0;
      const variables = executed.filter(c => nameOf(c) === 'create_variable');
      const blocks = executed.filter(c => nameOf(c) === 'create_block');
      const elements = executed.filter(c => nameOf(c) === 'insert_element');
      const delElements = executed.filter(c => nameOf(c) === 'delete_element');
      const delBlocks = executed.filter(c => nameOf(c) === 'delete_block');
      const delVariables = executed.filter(c => nameOf(c) === 'delete_variable');
      const parts = [];
      if (variables.length) {
        const names = variables.map(c => `"${(c.args || c.arguments || c.input).variable_name}"`).join(', ');
        parts.push(`${variables.length} variable${variables.length > 1 ? 's' : ''} créée${variables.length > 1 ? 's' : ''} (${names})`);
      }
      if (blocks.length) {
        const names = blocks.map(c => `"${(c.args || c.arguments || c.input).blockname}"`).join(', ');
        parts.push(`${blocks.length} bloc${blocks.length > 1 ? 's' : ''} créé${blocks.length > 1 ? 's' : ''} (${names})`);
      }
      if (elements.length) {
        parts.push(`${elements.length} élément${elements.length > 1 ? 's' : ''} inséré${elements.length > 1 ? 's' : ''}`);
      }
      if (delElements.length) {
        parts.push(`${delElements.length} élément${delElements.length > 1 ? 's' : ''} supprimé${delElements.length > 1 ? 's' : ''}`);
      }
      if (delBlocks.length) {
        const names = delBlocks.map(c => `"${(c.args || c.arguments || c.input).blockname || (c.args || c.arguments || c.input).idblock}"`).join(', ');
        parts.push(`${delBlocks.length} bloc${delBlocks.length > 1 ? 's' : ''} supprimé${delBlocks.length > 1 ? 's' : ''} (${names})`);
      }
      if (delVariables.length) {
        const names = delVariables.map(c => `"${(c.args || c.arguments || c.input).slug}"`).join(', ');
        parts.push(`${delVariables.length} variable${delVariables.length > 1 ? 's' : ''} supprimée${delVariables.length > 1 ? 's' : ''} (${names})`);
      }
      let summary = parts.length ? `Voilà ! J'ai ${parts.join(', ')}.` : '';
      if (failedCount > 0) {
        const failNote = `⚠️ ${failedCount} action${failedCount > 1 ? 's' : ''} n'${failedCount > 1 ? 'ont' : 'a'} pas abouti — voir le détail ci-dessous.`;
        summary = summary
          ? `${summary}\n\n${failNote}`
          : `Je n'ai pas pu exécuter les actions demandées. ${failNote}`;
      }
      return summary || 'Fait.';
    },

    _authHeaders() {
      const userData = localStorage.getItem('user');
      const token = userData ? JSON.parse(userData).data.token : '';
      return token ? { 'Authorization': `Bearer ${token}` } : {};
    },

    // Auto-test : Moo parcourt le bot qu'il vient de construire (via le moteur de simulation,
    // le même qu'agenttalk), détecte les étapes mortes, applique les corrections sûres et
    // propose le reste. Déclenché automatiquement quand un build se termine (plan complet).
    async runSelfTest() {
      if (!this.idbot) return;
      const idx = this.messages.push({
        role: 'ai', content: '', status: 'loading', statusText: '🔍 Moo teste le parcours…',
        isExecuting: false, warnings: [], options: [], optionsUsed: false, incompletePlan: false,
        createdBlocksList: [], continueUsed: false, showTestButton: false, usage: null,
        reasoning: '', reasoningOpen: false, selfTestFixes: [], selfTestApplied: false, selfTestApplying: false
      }) - 1;
      try {
        const res = await axiosMoo.post(`/ai/selftest/${this.idbot}`, { apply: 'safe' }, { headers: this._authHeaders() });
        const d = res.data || {};
        const m = this.messages[idx];
        m.status = null;
        const issues = d.issues || [];
        const applied = d.applied || [];
        const proposed = (d.proposed || []).filter(f => f.target);
        if (d.ok || !issues.length) {
          m.content = '✅ J\'ai testé tout le parcours : aucune étape morte détectée.';
        } else {
          let txt = `🔍 J'ai testé le parcours et trouvé ${issues.length} étape(s) morte(s).`;
          if (applied.length) txt += `\n✔ ${applied.length} correction(s) sûre(s) appliquée(s) automatiquement.`;
          if (proposed.length) {
            txt += `\n\nJe propose ${proposed.length} correction(s) :`;
            proposed.forEach(f => {
              const what = f.kind === 'add_redirect'
                ? `ajouter une redirection depuis « ${f.blockname} »`
                : `corriger « ${f.optionTitle || ''} » dans « ${f.blockname} »`;
              txt += `\n• ${what} → « ${f.targetName || f.target} »`;
            });
            m.selfTestFixes = proposed;
          } else if (!applied.length) {
            txt += `\n\n${d.reportBefore || ''}`;
          }
          m.content = txt;
        }
      } catch (e) {
        const m = this.messages[idx];
        if (m) { m.status = null; m.content = '⚠️ Auto-test indisponible : ' + (e.response?.data?.message || e.message); }
      }
    },

    // Applique les corrections proposées (confirmation utilisateur) puis re-vérifie.
    async applySelfTestFixes(msg) {
      if (!msg.selfTestFixes || !msg.selfTestFixes.length || !this.idbot) return;
      msg.selfTestApplying = true;
      try {
        const fixes = msg.selfTestFixes.map(f => ({ kind: f.kind, idblock: f.idblock, optionId: f.optionId, target: f.target }));
        const res = await axiosMoo.post(`/ai/selftest/${this.idbot}/apply`, { fixes }, { headers: this._authHeaders() });
        const d = res.data || {};
        msg.selfTestApplied = true;
        msg.content += `\n\n✔ ${d.saved || fixes.length} correction(s) appliquée(s). ` +
          (d.ok ? 'Parcours vérifié : aucune étape morte. 🎉' : `Reste ${d.remaining} point(s) à revoir.`) +
          `\n↻ Recharge le Playground pour voir les liens corrigés.`;
        this.$emit('reload-flow');
      } catch (e) {
        msg.content += `\n⚠️ Échec de l'application : ` + (e.response?.data?.message || e.message);
      } finally {
        msg.selfTestApplying = false;
      }
    },
    formatConvDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      const now = new Date();
      const diffMs = now - d;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'à l\'instant';
      if (diffMin < 60) return `il y a ${diffMin} min`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `il y a ${diffH} h`;
      const diffD = Math.floor(diffH / 24);
      if (diffD < 7) return `il y a ${diffD} j`;
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    },
    hydrateMessage(saved) {
      if (!saved) return null;
      if (saved.role === 'user') {
        return { role: 'user', content: saved.content || '', snapshot: null, file: saved.file || null };
      }
      return {
        role: 'ai',
        content: saved.content || '',
        status: null,
        statusText: null,
        isExecuting: false,
        warnings: [],
        options: saved.options || [],
        optionsUsed: true,
        incompletePlan: false,
        createdBlocksList: [],
        continueUsed: false,
        showTestButton: saved.showTestButton || false,
        usage: saved.usage || null,
        reasoning: saved.reasoning || '',
        reasoningOpen: false
      };
    },
    async loadConversations({ autoOpen = false } = {}) {
      if (!this.idbot) return;
      this.conversationsLoading = true;
      try {
        const res = await axiosMoo.get('/ai/conversations', {
          params: { idbot: this.idbot },
          headers: this._authHeaders()
        });
        if (res.data && res.data.success) {
          this.conversations = res.data.conversations || [];
          if (autoOpen && !this.idconversation && this.conversations.length > 0) {
            await this.loadConversation(this.conversations[0].idconversation);
          }
        }
      } catch (e) {
        console.error('[Moo] Erreur loadConversations:', e);
      } finally {
        this.conversationsLoading = false;
      }
    },
    async loadConversation(idconversation) {
      if (!idconversation) return;
      try {
        const res = await axiosMoo.get(`/ai/conversations/${encodeURIComponent(idconversation)}`, {
          headers: this._authHeaders()
        });
        if (res.data && res.data.success && res.data.conversation) {
          const conv = res.data.conversation;
          this.idconversation = conv.idconversation;
          this.messages = (conv.messages || []).map(m => this.hydrateMessage(m)).filter(Boolean);
          this.showConvPanel = false;
          this.scrollToBottom();
        }
      } catch (e) {
        console.error('[Moo] Erreur loadConversation:', e);
      }
    },
    newConversation() {
      this.idconversation = null;
      this.messages = [];
      this.showConvPanel = false;
      this.$nextTick(() => {
        if (this.$refs.promptInput) this.$refs.promptInput.focus();
      });
    },
    async deleteConversation(conv) {
      if (!conv || !conv.idconversation) return;
      if (!confirm(`Supprimer la conversation "${conv.title || 'Sans titre'}" ?`)) return;
      try {
        await axiosMoo.delete(`/ai/conversations/${encodeURIComponent(conv.idconversation)}`, {
          headers: this._authHeaders()
        });
        this.conversations = this.conversations.filter(c => c.idconversation !== conv.idconversation);
        if (this.idconversation === conv.idconversation) {
          this.idconversation = null;
          this.messages = [];
        }
      } catch (e) {
        console.error('[Moo] Erreur deleteConversation:', e);
      }
    },
    startRename(conv) {
      this.renamingConvId = conv.idconversation;
      this.renamingTitle = conv.title || '';
      this.$nextTick(() => {
        const input = Array.isArray(this.$refs.renameInput) ? this.$refs.renameInput[0] : this.$refs.renameInput;
        if (input) { input.focus(); input.select(); }
      });
    },
    cancelRename() {
      this.renamingConvId = null;
      this.renamingTitle = '';
    },
    async confirmRename(conv) {
      const newTitle = (this.renamingTitle || '').trim();
      if (!newTitle || newTitle === conv.title) {
        this.cancelRename();
        return;
      }
      try {
        await axiosMoo.patch(
          `/ai/conversations/${encodeURIComponent(conv.idconversation)}`,
          { title: newTitle },
          { headers: this._authHeaders() }
        );
        const item = this.conversations.find(c => c.idconversation === conv.idconversation);
        if (item) item.title = newTitle;
      } catch (e) {
        console.error('[Moo] Erreur confirmRename:', e);
      } finally {
        this.cancelRename();
      }
    },

    async sendMessage() {
      if ((!this.inputPrompt.trim() && !this.attachedFile) || this.isProcessing) return;

      const prompt = this.inputPrompt;
      const fileToSend = this.attachedFile;
      // Tour d'exécution (confirmation/continuation) -> on coupe la réflexion côté backend
      const noThinking = !!this._executePhase;
      this._executePhase = false;
      this.inputPrompt = '';
      this.attachedFile = null;
      this.clearAttachedPreview();
      this.closeMentionMenu();

      // On ré-injecte l'URL/type des fichiers importés dans l'historique : sinon
      // l'URL (présente seulement dans le prompt one-shot de /ai/build-file) est
      // perdue aux tours suivants et Moo redemande le lien de l'image.
      const chatHistory = this.messages
        .filter(m => m.status !== 'error' && m.status !== 'loading')
        .map(m => {
          let content = m.content || '';
          if (m.file && m.file.url) {
            content += `${content ? '\n' : ''}[Fichier importé : ${m.file.name} · à insérer comme élément "${m.file.elementType || this.fileKindFromName(m.file.name)}" · URL absolue : ${m.file.url}]`;
          }
          return { role: m.role === 'ai' ? 'assistant' : 'user', content };
        });

      this.$nextTick(() => {
        if (this.$refs.promptInput) this.$refs.promptInput.style.height = 'auto';
      });

      // Prompt injecté automatiquement (continuation / clic bouton de plan) -> affiché
      // replié et grisé plutôt que comme un message tapé par l'utilisateur. On lit puis
      // réinitialise le flag pour ne pas le faire fuir sur le message suivant.
      const isInjected = !!this._injectedPrompt;
      this._injectedPrompt = false;
      this.messages.push({
        role: 'user',
        content: prompt,
        snapshot: null,
        injected: isInjected,
        injectedOpen: false,
        file: fileToSend ? { name: fileToSend.name, kind: this.fileKindFromName(fileToSend.name) } : null
      });
      const userMsgIndex = this.messages.length - 1;

      const aiMsgIndex = this.messages.length;
      this.messages.push({ role: 'ai', content: '', status: 'loading', statusText: 'Réflexion', isExecuting: false, warnings: [], options: [], optionsUsed: false, incompletePlan: false, createdBlocksList: [], continueUsed: false, showTestButton: false, usage: null, reasoning: '', reasoningOpen: false });

      this.isProcessing = true;

      // Animation de réflexion
      const thinkingSteps = [
        'Ruminating...',
        'mbeeeuuuuu...',
        'nantchooo...',
        'wouy sama ndeye...',
        'piiiponnnn...',
        'xalatingg...',
        'totch seuh bopping...',
        'kane lagn ko changeii....',
        'xeel bagui natching...',
        'khodjootinaaa...',
        'reflechiring...',
        'nanditeying...',
        'sheuuteuuteut',
        'tchiiiiippp...',
        'aywa telecommandeuhh... commandeuh tele yii',
        'xelyinngggg ataya',
        'legui ma pareii',
        'boul coupe magui nieuw ',
        'mieuawou'
      ];
      let stepIndex = 0;
      // Mélange l'ordre pour varier d'une fois à l'autre
      const shuffledSteps = [...thinkingSteps].sort(() => Math.random() - 0.5);
      this.messages[aiMsgIndex].statusText = shuffledSteps[0];
      // Durées variées et plus longues : chaque mot reste affiché un temps différent
      // (~1.6 s à 4 s), pour un effet de réflexion naturel plutôt qu'un défilement mécanique.
      let thinkingTimeout = null;
      const scheduleThinkingStep = () => {
        const delay = 1600 + Math.random() * 2400;
        thinkingTimeout = setTimeout(() => {
          if (this.messages[aiMsgIndex]) {
            stepIndex++;
            this.messages[aiMsgIndex].statusText = shuffledSteps[stepIndex % shuffledSteps.length];
            scheduleThinkingStep();
          }
        }, delay);
      };
      scheduleThinkingStep();

      try {
        const userData = localStorage.getItem('user');
        const token = userData ? JSON.parse(userData).data.token : '';

        const minWaitPromise = new Promise(resolve => setTimeout(resolve, 2500));

        // Extraire les blocs mentionnés avec @ dans le prompt — envoyer le bloc complet avec son flow
        const mentionedBlocks = [];
        for (const block of (this.currentBlocks || [])) {
          const name = (block.blockname || block.displayName || '').toLowerCase();
          if (name && prompt.toLowerCase().includes('@' + name)) {
            mentionedBlocks.push(block); // objet complet avec flow
          }
        }

        const existingBlocks = (this.currentBlocks || []).map(b => ({
          idblock: b.idblock,
          blockname: b.blockname || b.displayName,
          type: b.type,
          description: b.description || ''
        }));
        const existingVariables = (this.currentVariables || []).map(v => ({
          slug: v.slug,
          type: v.type
        }));

        let responsePromise;
        if (fileToSend) {
          // Envoi multipart vers /ai/build-file : Moo extrait le contenu et propose un plan
          const fd = new FormData();
          fd.append('file', fileToSend);
          fd.append('chatbotId', this.chatbotId);
          if (this.idbot) fd.append('idbot', this.idbot);
          if (prompt && prompt.trim()) fd.append('prompt', prompt);
          fd.append('history', JSON.stringify(chatHistory));
          fd.append('currentBlockFlow', JSON.stringify(this.currentBlockFlow || null));
          fd.append('mentionedBlocks', JSON.stringify(mentionedBlocks));
          fd.append('selectedElement', JSON.stringify(this.selectedElement || null));
          fd.append('constitution', JSON.stringify(this.constitution || null));
          if (this.idconversation) fd.append('idconversation', this.idconversation);
          fd.append('existingBlocks', JSON.stringify(existingBlocks));
          fd.append('existingVariables', JSON.stringify(existingVariables));
          responsePromise = axiosMoo.post(`/ai/build-file`, fd, {
            headers: { 'Authorization': `Bearer ${token}` },
            // axiosMoo force Content-Type: application/json (ce qui convertirait le
            // FormData en JSON et perdrait le fichier). On retire l'en-tête ici pour
            // que le navigateur pose lui-même le boundary multipart/form-data.
            transformRequest: [(data, headers) => {
              if (headers && typeof headers.delete === 'function') headers.delete('Content-Type');
              else if (headers) { delete headers['Content-Type']; delete headers['content-type']; }
              return data;
            }]
          });
        } else {
          responsePromise = axiosMoo.post(`/ai/build`, {
            chatbotId: this.chatbotId,
            idbot: this.idbot,
            prompt,
            history: chatHistory,
            currentBlockFlow: this.currentBlockFlow,
            mentionedBlocks,
            selectedElement: this.selectedElement || null,
            constitution: this.constitution || null,
            idconversation: this.idconversation,
            existingBlocks,
            existingVariables,
            noThinking
          }, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }

        const [response] = await Promise.all([responsePromise, minWaitPromise]);

        clearTimeout(thinkingTimeout);

        if (response.data && response.data.success) {
          const commands = response.data.actions;
          const usage = response.data.usage;
          const fallbackText = response.data.text || '';
          // Métadonnées fichier renvoyées par le backend (type réel, troncature) -> chip
          if (response.data.file && this.messages[userMsgIndex]) {
            this.messages[userMsgIndex].file = response.data.file;
          }
          // Réflexion du modèle (extended thinking) — affichée dans le dropdown
          this.messages[aiMsgIndex].reasoning = response.data.reasoning || '';
          if (usage) {
            this.messages[aiMsgIndex].usage = usage;
            this.sessionTokens.input += Math.max(0, (usage.input_tokens || 0) - this.systemPromptTokens);
            this.sessionTokens.output += usage.output_tokens || 0;
          }

          // Synchronisation de la conversation (création auto côté serveur si besoin)
          const isNewConv = response.data.idconversation && response.data.idconversation !== this.idconversation;
          if (response.data.idconversation) {
            this.idconversation = response.data.idconversation;
          }
          if (isNewConv || response.data.title) {
            this.loadConversations();
          }

          console.log('%c[MooAI] Réponse backend reçue', 'color: #DAFFD1; font-weight: bold;');
          console.log('[MooAI] Nombre d\'actions :', commands.length);
          commands.forEach((cmd, i) => {
            console.log(`[MooAI] Action ${i + 1} — ${cmd.function || cmd.name}`, cmd.args || cmd.arguments || cmd.input);
          });

          const conversationCmd = commands.find(c => (c.function || c.name) === 'respond_to_user');
          const clarificationCmd = commands.find(c => (c.function || c.name) === 'ask_clarification');
          const buildCommands = commands.filter(c => !['respond_to_user', 'ask_clarification'].includes(c.function || c.name));

          if (clarificationCmd) {
            // Moo pose une question ou présente un plan — afficher avec boutons.
            // Le PLAN détaillé est souvent dans le texte libre (fallbackText) tandis que
            // le message du tool est un court récap : on combine les deux pour l'afficher
            // ET le conserver dans l'historique (sinon Moo perd le plan à la confirmation).
            const args = clarificationCmd.args || clarificationCmd.arguments || clarificationCmd.input;
            const askMsg = (args && args.message) || '';
            this.messages[aiMsgIndex].status = 'success';
            this.messages[aiMsgIndex].statusText = 'Terminé';
            this.messages[aiMsgIndex].content = [fallbackText, askMsg]
              .map(s => (s || '').trim())
              .filter(Boolean)
              .filter((s, i, a) => a.indexOf(s) === i)
              .join('\n\n') || askMsg;
            this.messages[aiMsgIndex].options = args.options || [];
            this.messages[aiMsgIndex].optionsUsed = false;
          } else if (conversationCmd && buildCommands.length === 0) {
            // Réponse conversationnelle pure (+ éventuel texte libre détaillé avant)
            const respMsg = (conversationCmd.args || conversationCmd.arguments || conversationCmd.input).message || '';
            this.messages[aiMsgIndex].status = 'success';
            this.messages[aiMsgIndex].statusText = 'Terminé';
            this.messages[aiMsgIndex].content = [fallbackText, respMsg]
              .map(s => (s || '').trim())
              .filter(Boolean)
              .filter((s, i, a) => a.indexOf(s) === i)
              .join('\n\n') || respMsg;
          } else if (buildCommands.length === 0) {
            // Aucune action exécutable : le modèle a juste réfléchi/écrit du texte libre.
            // On affiche ce texte au lieu de lancer une exécution vide (fini les "Fait." vides).
            this.messages[aiMsgIndex].status = 'success';
            this.messages[aiMsgIndex].statusText = 'Terminé';
            this.messages[aiMsgIndex].content = fallbackText || "Je n'ai pas d'action à exécuter pour cette demande.";
          } else {
            // Actions de construction — garder le spinner actif pendant toute l'exécution
            this.executionCancelled = false;
            this.messages[aiMsgIndex].status = 'loading';
            this.messages[aiMsgIndex].statusText = 'Exécution en cours...';
            this.messages[aiMsgIndex].isExecuting = true;
            const self = this;
            const controller = {
              get cancelled() { return self.executionCancelled; },
              onProgress(label) {
                if (self.messages[aiMsgIndex]) {
                  self.messages[aiMsgIndex].statusText = label;
                }
              }
            };
            this.$emit('execute-commands', buildCommands, (warnings, snapshot, results) => {
              self.messages[aiMsgIndex].isExecuting = false;
              self.messages[aiMsgIndex].status = null;
              self.messages[aiMsgIndex].content = self.buildActionSummary(buildCommands, results);
              self.messages[aiMsgIndex].showTestButton = true;
              if (warnings && warnings.length > 0) {
                self.messages[aiMsgIndex].warnings = warnings;
              }
              if (snapshot && self.messages[userMsgIndex]) {
                self.messages[userMsgIndex].snapshot = snapshot;
                console.log('%c[MooAI UNDO] snapshot stocké sur msg', 'color:#DAFFD1', 'userMsgIndex:', userMsgIndex, snapshot);
              }
              // Détection plan incomplet : bloc(s) créé(s) n'ayant reçu AUCUN insert_element
              // (et pas seulement le cas "zéro insertion" — une réponse tronquée peut avoir
              // rempli les premiers blocs et laissé les suivants vides)
              const createdBlocks = buildCommands.filter(c => (c.function || c.name) === 'create_block');
              const normName = n => String(n || '').trim().toLowerCase();
              const insertTargets = new Set(
                buildCommands
                  .filter(c => (c.function || c.name) === 'insert_element')
                  .map(c => normName(((c.args || c.arguments || c.input) || {}).blockname))
              );
              const unfilledBlocks = createdBlocks
                .map(c => ((c.args || c.arguments || c.input) || {}).blockname)
                .filter(name => name && !insertTargets.has(normName(name)));
              // plan_incomplete : le backend signale que sa boucle de continuation a été
              // coupée par le plafond d'appels sans conclusion du modèle — des blocs du
              // plan peuvent ne pas encore exister, relancer pour repartir avec un budget neuf
              const backendIncomplete = !!(response.data && response.data.plan_incomplete);
              if (unfilledBlocks.length > 0 || backendIncomplete) {
                self.messages[aiMsgIndex].incompletePlan = true;
                self.messages[aiMsgIndex].createdBlocksList = unfilledBlocks;
                console.log('%c[MooAI] Plan incomplet détecté', 'color:#DAFFD1;font-weight:bold', 'blocs créés sans éléments:', unfilledBlocks, '| backend plan_incomplete:', backendIncomplete);

                // Anti-boucle : max 5 auto-continues consécutifs (gros plans construits
                // par lots à cause du budget de temps backend), ensuite bouton manuel.
                let chainedCount = 0;
                for (let i = aiMsgIndex - 1; i >= 0; i--) {
                  const m = self.messages[i];
                  if (!m || m.role !== 'ai') continue;
                  if (m.incompletePlan && m.continueUsed) chainedCount++;
                  else break;
                }
                const alreadyChained = chainedCount >= 8;

                if (alreadyChained) {
                  console.log('%c[MooAI] Auto-continue ignoré (déjà tenté à l\'itération précédente)', 'color:#ff8080;font-weight:bold');
                } else {
                  // Auto-continue : déclenche la suite sans intervention utilisateur
                  setTimeout(() => {
                    const msg = self.messages[aiMsgIndex];
                    if (msg && !msg.continueUsed && !self.isProcessing) {
                      console.log('%c[MooAI] Auto-continue activé', 'color:#7be07b;font-weight:bold');
                      self.continuePlan(msg);
                    }
                  }, 600);
                }
              } else {
                // Plan COMPLET (aucun bloc vide, backend non incomplet) :
                // Moo auto-teste le parcours qu'il vient de construire (étapes mortes).
                self.runSelfTest(aiMsgIndex);
              }
            }, controller);
          }
        } else {
          throw new Error(response.data.message || 'Erreur inconnue');
        }

      } catch (error) {
        clearTimeout(thinkingTimeout);
        console.error('Erreur Copilot:', error);
        const errorMsg = error.response?.data?.message || error.message || 'Erreur inconnue';
        
        this.messages[aiMsgIndex].status = 'error';
        this.messages[aiMsgIndex].statusText = 'Action interrompue';
        this.messages[aiMsgIndex].content = `⚠️ Je n'ai pas pu finaliser l'action.\n\nDétail technique : ${errorMsg}`;
      } finally {
        this.isProcessing = false;
        setTimeout(() => {
          if (this.messages[aiMsgIndex] && !this.messages[aiMsgIndex].isExecuting) {
            this.messages[aiMsgIndex].status = null;
          }
        }, 3000);
      }
    }
  }
}
</script>

<style>
/* Custom subtle scrollbars for the whole AI Sidebar */
.ai-sidebar *::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}
.ai-sidebar *::-webkit-scrollbar-track {
  background: transparent;
}
.ai-sidebar *::-webkit-scrollbar-thumb {
  background: rgba(218, 255, 209, 0.15);
  border-radius: 10px;
}
.ai-sidebar *::-webkit-scrollbar-thumb:hover {
  background: rgba(218, 255, 209, 0.3);
}

/* Décale le contenu du playground quand la sidebar est ouverte */
body:has(.ai-sidebar.is-open) .flow-element-wrapper {
  margin: 4rem 15rem !important;
}

/* Global style to override any scoped interference */
.ai-sidebar {
  position: fixed;
  top: 0;
  right: -450px;
  width: 450px;
  height: 100vh;
  background: #0A1A16 !important; /* Deep Dark to match sidebar */
  border-left: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2147483647;
  box-shadow: -10px 0 30px rgba(0,0,0,0.5);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #cccccc;
}

.ai-sidebar.is-open {
  right: 0;
}

/* Mobile : Moo occupe tout l'écran (plus une simple barre latérale). */
@media (max-width: 768px) {
  .ai-sidebar {
    width: 100vw;
    right: -100vw;
    border-left: none;
    /* 100vh déborde sous la barre du navigateur mobile -> dvh = hauteur réellement visible.
       On réserve aussi ~66px (+ safe-area) pour la barre d'onglets Moo/Tester du playground,
       sinon le champ de saisie passe dessous. */
    height: calc(100vh - 66px - env(safe-area-inset-bottom, 0px));
    height: calc(100dvh - 66px - env(safe-area-inset-bottom, 0px));
    /* place sous le widget de test (2147483647) pour que la bulle reste tappable */
    z-index: 2147483000;
  }
  .ai-sidebar.is-open { right: 0; }
  /* Sur mobile on ne peut PAS fermer Moo : le dashboard derrière n'est pas
     utilisable sur téléphone — la navigation passe par la barre d'onglets. */
  .ai-sidebar .close-sidebar-btn { display: none !important; }
}

/* Overlay de glisser-déposer d'un fichier dans la discussion */
.moo-drop-overlay {
  position: absolute;
  inset: 8px;
  z-index: 2147483646;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed rgba(218, 255, 209, 0.55);
  border-radius: 14px;
  background: rgba(10, 26, 22, 0.86);
  backdrop-filter: blur(3px);
  /* Laisse passer les événements drag vers la sidebar (évite le flicker) */
  pointer-events: none;
  animation: mooDropFade 0.15s ease;
}
@keyframes mooDropFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.moo-drop-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  color: #DAFFD1;
  padding: 0 24px;
}
.moo-drop-inner svg {
  opacity: 0.9;
}
.moo-drop-title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.moo-drop-sub {
  font-size: 0.8rem;
  color: rgba(218, 255, 209, 0.6);
}

.ai-chat-area {
  flex: 1;
  overflow-y: auto;
  padding: 24px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #0A1A16 !important;
}

.token-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 20px;
  background: transparent;
  border-top: 1px solid rgba(218, 255, 209, 0.08);
  font-size: 0.75rem;
  color: rgba(218, 255, 209, 0.5);
  font-family: 'SF Mono', monospace;
  letter-spacing: 0.02em;
  user-select: none;
}
.token-item { white-space: nowrap; display: flex; align-items: center; gap: 4px; color: rgba(218, 255, 209, 0.6); }
.token-item svg { color: rgba(218, 255, 209, 0.4); }
.token-sep { color: rgba(218, 255, 209, 0.2); }

.ai-input-area {
  position: relative;
  padding: 16px 20px 24px 20px;
  background: #0A1A16 !important;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.moo-welcome {
  color: #DAFFD1;
  font-size: 1.6rem;
  font-weight: 800;
  margin-bottom: 16px;
}

.moo-question {
  color: #DAFFD1;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 24px;
}

.hint-pill {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #888;
}

.user-msg .message-content {
  background: #143028; /* Peelo Secondary Dark */
  color: #e0e0e0;
  padding: 14px 18px;
  border-radius: 16px;
  font-size: 0.95rem;
  line-height: 1.5;
  border: 1px solid rgba(218, 255, 209, 0.05);
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.msg-text {
  flex: 1;
  word-break: break-word;
  line-height: 1.55;
}
/* ===== Rendu Markdown des messages Moo ===== */
.msg-text strong { font-weight: 700; color: inherit; }
.ai-msg .msg-text strong { color: #eafff2; }

/* Paragraphes & espacement */
.msg-text .md-p { margin: 2px 0; }
.msg-text .md-p + .md-p { margin-top: 6px; }
.msg-text .md-sp { height: 8px; }

/* Titres */
.msg-text .md-h {
  font-weight: 800;
  font-size: 0.97rem;
  letter-spacing: 0.01em;
  margin: 12px 0 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(218, 255, 209, 0.18);
}
.msg-text .md-h:first-child { margin-top: 0; }
.ai-msg .msg-text .md-h { color: #DAFFD1; }

/* Listes */
.msg-text .md-ul,
.msg-text .md-ol { margin: 4px 0 4px 2px; padding-left: 18px; display: flex; flex-direction: column; gap: 4px; }
.msg-text .md-ul { list-style: none; padding-left: 4px; }
.msg-text .md-ul > li { position: relative; padding-left: 16px; }
.msg-text .md-ul > li::before {
  content: '';
  position: absolute;
  left: 2px; top: 0.62em;
  width: 5px; height: 5px;
  border-radius: 50%;
  background: #6fcf97;
}
.msg-text .md-ol > li { padding-left: 2px; }
.msg-text .md-ol > li::marker { color: #6fcf97; font-weight: 700; }

/* Code inline */
.msg-text .md-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.84em;
  padding: 1.5px 6px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.user-msg .msg-text .md-code { background: rgba(255, 255, 255, 0.18); border-color: rgba(255, 255, 255, 0.15); }

/* Séparateur */
.msg-text .md-hr { border: none; border-top: 1px solid rgba(255, 255, 255, 0.12); margin: 10px 0; }

/* Tableaux */
.msg-text .md-table {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0;
  font-size: 0.85rem;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.msg-text .md-table th,
.msg-text .md-table td {
  text-align: left;
  padding: 6px 9px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  vertical-align: top;
}
.msg-text .md-table th { background: rgba(218, 255, 209, 0.12); font-weight: 700; color: #DAFFD1; }
.msg-text .md-table tr:last-child td { border-bottom: none; }
.msg-text .md-table th:last-child,
.msg-text .md-table td:last-child { border-right: none; }
.msg-text .md-table tbody tr:nth-child(even) td { background: rgba(255, 255, 255, 0.03); }

.edit-msg-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s, background 0.2s;
}

.edit-msg-btn:hover {
  color: #DAFFD1;
  background: rgba(218, 255, 209, 0.1);
}

/* Modern Input Area Design */
.selected-element-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  margin-bottom: 6px;
  background: rgba(218, 255, 209, 0.08);
  border: 1px solid rgba(218, 255, 209, 0.2);
  border-radius: 8px;
  color: #DAFFD1;
  font-size: 0.75rem;
  line-height: 1.3;
}
.selected-element-chip svg { flex-shrink: 0; opacity: 0.7; }
.chip-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chip-clear {
  background: none;
  border: none;
  color: rgba(218, 255, 209, 0.5);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 2px;
  flex-shrink: 0;
}
.chip-clear:hover { color: #DAFFD1; }

/* Chip fichier joint (en attente d'envoi) — réutilise le style des chips */
.attached-file-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  margin-bottom: 6px;
  background: rgba(218, 255, 209, 0.08);
  border: 1px solid rgba(218, 255, 209, 0.2);
  border-radius: 8px;
  color: #DAFFD1;
  font-size: 0.75rem;
  line-height: 1.3;
}
.attached-file-chip .file-emoji { flex-shrink: 0; }
.attached-file-block { display: flex; flex-direction: column; gap: 6px; margin-bottom: 6px; }
.attached-file-preview {
  max-width: 140px;
  max-height: 120px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid rgba(218, 255, 209, 0.25);
}

/* Chip fichier affiché dans une bulle de la discussion */
.msg-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 5px 10px;
  margin-bottom: 6px;
  background: rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  font-size: 0.78rem;
}
.user-msg .msg-file-chip {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.28);
}
.msg-file-chip .file-emoji { flex-shrink: 0; }
.msg-file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.msg-file-link { color: inherit; text-decoration: underline; cursor: pointer; }
.msg-file-link:hover { opacity: 0.85; }

/* Bloc fichier (miniature image + chip) dans la discussion */
.msg-file-block { display: flex; flex-direction: column; gap: 6px; margin-bottom: 6px; }
.msg-file-thumb {
  display: block;
  max-width: 180px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.user-msg .msg-file-thumb { border-color: rgba(255, 255, 255, 0.28); }
.msg-file-thumb img { display: block; width: 100%; height: auto; max-height: 180px; object-fit: cover; }
.msg-file-trunc {
  flex-shrink: 0;
  font-size: 0.65rem;
  opacity: 0.7;
  padding: 1px 5px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.12);
}

.input-wrapper.modern-chat-input {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: border-color 0.2s, background 0.2s;
}

.input-wrapper.modern-chat-input:focus-within {
  border-color: rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.07);
}

.modern-chat-input textarea {
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  padding: 4px 4px 12px 4px;
  font-size: 0.95rem;
  color: #FFFFFF;
  line-height: 1.5;
  max-height: 150px;
  min-height: 24px;
  font-family: inherit;
}

.modern-chat-input textarea:focus { outline: none; }

.modern-chat-input textarea::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.input-actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.actions-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-btn.plus-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
}

.icon-btn.plus-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
}

.plus-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  background: #0A1A16;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 6px;
  min-width: 140px;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.5);
  z-index: 110;
}

.plus-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  color: #FFFFFF;
  font-size: 0.9rem;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.1s;
}

.plus-menu-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.model-badge {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 500;
  cursor: default;
}

.modern-send-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #DAFFD1; /* Peelo Mint */
  border: none;
  color: #0A1A16;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.modern-send-btn:hover:not(:disabled) {
  background: #C4FCA9;
  transform: scale(1.05);
}

.modern-send-btn:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.3);
  cursor: not-allowed;
}

.mention-menu {
  position: absolute;
  bottom: calc(100% - 10px);
  left: 20px;
  width: calc(100% - 40px);
  background: #0A1A16;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  z-index: 100;
  box-shadow: 0 -8px 30px rgba(0,0,0,0.5);
  overflow: hidden;
}
</style>

<style scoped>
/* Scoped styles for internal logic only */

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 40px 20px;
  position: relative;
  overflow: hidden;
}

.empty-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(218, 255, 209, 0.05) 0%, rgba(10, 26, 22, 0) 70%);
  pointer-events: none;
  z-index: 0;
}

.empty-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  animation: contentFadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes contentFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.moo-brand-logo {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #143028 0%, #0A1A16 100%);
  border: 1px solid rgba(218, 255, 209, 0.2);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #DAFFD1;
  font-size: 28px;
  font-weight: 900;
  margin-bottom: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.moo-welcome {
  color: #FFFFFF;
  font-size: 1.8rem;
  font-weight: 800;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
}

.moo-question {
  color: #DAFFD1;
  opacity: 0.7;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 40px;
}

.suggestions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  max-width: 320px;
  margin-bottom: 40px;
}

.suggestion-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 16px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.suggestion-card:hover {
  background: rgba(218, 255, 209, 0.05);
  border-color: rgba(218, 255, 209, 0.2);
  transform: translateY(-2px);
}

.sugg-icon {
  font-size: 18px;
}

.suggestion-card span:last-child {
  font-size: 12px;
  font-weight: 600;
  color: #e0e0e0;
}

.hint-pill {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 8px 20px;
  border-radius: 100px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 4px;
}

.at-symbol {
  color: #DAFFD1;
  font-weight: 800;
  background: rgba(218, 255, 209, 0.1);
  padding: 0 6px;
  border-radius: 4px;
}

.message-wrapper { display: flex; width: 100%; }
.user-msg { justify-content: flex-end; }
.ai-msg { justify-content: flex-start; }

.bubble-stack {
  display: flex;
  flex-direction: column;
  max-width: 90%;
  gap: 3px;
}
.bubble-stack-end { align-items: flex-end; }
.bubble-stack-start { align-items: flex-start; }

.undo-btn-row {
  display: flex;
  justify-content: flex-end;
  padding-right: 4px;
}

.undo-icon-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  padding: 3px 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s, background 0.2s;
}

.undo-icon-btn:hover {
  color: #DAFFD1;
  background: #143028;
}

.message-bubble { display: flex; gap: 12px; align-items: flex-start; width: 100%; }
.user-msg .message-bubble { flex-direction: row-reverse; }

.ai-avatar {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
}

/* ---------- Mention Menu Premium CSS ---------- */

.mention-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  font-style: italic;
}

.mention-section-title {
  padding: 8px 14px 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(218, 255, 209, 0.5);
}

.mention-section-title.var {
  color: rgba(96, 165, 250, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  margin-top: 4px;
  padding-top: 10px;
}

.mention-item {
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: background 0.1s;
}

.mention-item:hover, .mention-item.is-selected {
  background: rgba(255, 255, 255, 0.05);
}

.mention-item-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.block-icon {
  background: rgba(218, 255, 209, 0.08);
  color: #DAFFD1;
}

.var-icon {
  background: rgba(96, 165, 250, 0.1);
  color: #60A5FA;
}

.mention-details {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-width: 0;
}

.mention-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #FFFFFF;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mention-sub {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.35);
  font-family: 'SF Mono', monospace;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mention-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 4px;
  flex-shrink: 0;
  letter-spacing: 0.04em;
}

.mention-badge.block {
  background: rgba(218, 255, 209, 0.1);
  color: #DAFFD1;
}

.mention-badge.variable {
  background: rgba(96, 165, 250, 0.1);
  color: #60A5FA;
}

.mention-empty {
  padding: 20px 14px;
  text-align: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85rem;
}

/* ---------- Claude Code Style Animations ---------- */
.msg-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 12px;
  width: fit-content;
}

.msg-status.loading {
  background: transparent;
}

.stop-execution-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  cursor: pointer;
  margin-left: 4px;
  transition: background 0.15s;
  white-space: nowrap;
}

.stop-execution-btn:hover {
  background: rgba(239, 68, 68, 0.22);
}

.msg-status.success {
  color: #DAFFD1;
  font-size: 0.85rem;
}

.msg-status.error {
  color: #ef4444;
  font-size: 0.85rem;
}

.bounce-loader {
  width: 20px;
  height: 22px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-shrink: 0;
}

.bounce-ball {
  width: 9px;
  height: 9px;
  background: #DAFFD1;
  border-radius: 50%;
  transform-origin: center bottom;
  animation: bounceBall 0.55s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite alternate;
}

@keyframes bounceBall {
  0% {
    transform: translateY(0) scaleX(1.45) scaleY(0.6);
  }
  100% {
    transform: translateY(-13px) scaleX(0.8) scaleY(1.2);
  }
}

.claude-shimmer {
  color: #DAFFD1;
  animation: pulseOpacity 1.5s ease-in-out infinite alternate;
  font-family: 'SF Mono', monospace, sans-serif;
  font-size: 0.85rem;
  letter-spacing: 0.03em;
  display: inline-block;
}

@keyframes pulseOpacity {
  0% { opacity: 0.4; }
  100% { opacity: 1; }
}

/* ---------- Reasoning Dropdown (extended thinking) ---------- */
.msg-reasoning {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 8px;
}

.msg-reasoning-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  background: rgba(218, 255, 209, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(218, 255, 209, 0.7);
  font-size: 0.78rem;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.2s ease;
}

.msg-reasoning-toggle:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #DAFFD1;
  border-color: rgba(218, 255, 209, 0.2);
}

.reasoning-spark {
  opacity: 0.9;
  color: #A9D18E;
}

.reasoning-chevron {
  transition: transform 0.2s ease;
  opacity: 0.7;
}
.reasoning-chevron.open {
  transform: rotate(180deg);
}

.msg-reasoning-body {
  margin-top: 8px;
  padding: 16px 18px;
  background: linear-gradient(145deg, rgba(20, 48, 40, 0.6), rgba(10, 26, 22, 0.8));
  border: 1px solid rgba(218, 255, 209, 0.15);
  border-left: 3px solid #DAFFD1;
  border-radius: 12px;
  color: rgba(218, 255, 209, 0.85);
  font-size: 0.85rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'SF Mono', ui-monospace, monospace;
  max-height: 350px;
  overflow-y: auto;
  box-shadow: inset 0 2px 8px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.1);
  animation: reasoningFade 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

/* ---------- Prompt injecté automatiquement (replié, grisé, réouvrable) ---------- */
/* Pas de bulle utilisateur : on neutralise le fond pour un rendu discret type "note". */
.message-bubble.injected-bubble {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
.injected-prompt {
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: flex-end; /* côté utilisateur */
}
.injected-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-end;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 3px 9px;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.72rem;
  font-weight: 500;
  font-style: italic;
  font-family: inherit;
  transition: all 0.2s ease;
}
.injected-toggle:hover {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.15);
}
.injected-spark { opacity: 0.6; }
.injected-see { opacity: 0.8; text-decoration: underline; text-underline-offset: 2px; }
.injected-chevron { transition: transform 0.2s ease; opacity: 0.6; }
.injected-chevron.open { transform: rotate(180deg); }
.injected-body {
  margin-top: 6px;
  max-width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.78rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
  animation: reasoningFade 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes reasoningFade {
  from { opacity: 0; transform: translateY(-4px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* ---------- Notification Warnings Style ---------- */
.msg-text-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.ai-msg .message-content {
  background: transparent;
  color: #e0e0e0;
}

.ai-msg .msg-text {
  font-size: 0.99rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.9;
  white-space: pre-wrap;
  word-break: break-word;
  padding-left: 10px;
}

.user-msg .msg-text {
  white-space: pre-wrap;
  line-height: 1.6;
}

.msg-warnings-box {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  width: 100%;
  margin-top: 4px;
}

.msg-warnings-header {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}

.msg-warnings-box ul {
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
  line-height: 1.5;
  color: white;
  font-weight: normal;
}

.msg-warnings-box li {
  margin-bottom: 3px;
}

.msg-warnings-footer {
  margin-top: 4px;
  font-weight: normal;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}

/* ---------- Boutons de clarification / plan ---------- */
.msg-block-tag {
  display: inline-flex;
  align-items: center;
  background: rgba(218, 255, 209, 0.08);
  color: #DAFFD1;
  border-radius: 5px;
  padding: 1px 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
  white-space: nowrap;
}

.msg-block-tag:hover {
  background: #143028;
}

.msg-options-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.msg-option-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 7px 16px;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s, opacity 0.15s;
  font-family: inherit;
  white-space: nowrap;
}

.msg-option-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.msg-option-btn.opt-primary {
  background: #DAFFD1;
  color: #0A1A16;
}

.msg-option-btn.opt-primary:hover:not(:disabled) {
  background: #C4FCA9;
  transform: translateY(-1px);
}

.msg-option-btn.opt-secondary {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.msg-option-btn.opt-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

.msg-token-count {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  margin-left: 10px;
  margin-top: -4px;
  background: rgba(218, 255, 209, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.1);
  border-radius: 20px;
  font-size: 0.7rem;
  font-family: 'SF Mono', monospace;
  letter-spacing: 0.02em;
  width: fit-content;
}
.tok-in  { color: rgba(218, 255, 209, 0.6); }
.tok-out { color: rgba(218, 255, 209, 0.4); }
.tok-sep { color: rgba(218, 255, 209, 0.2); }

/* ====================================================
   Conversation history (sidebar header + glassmorphic modal)
   ==================================================== */
.moo-conv-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
}

.moo-brand-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.moo-brand-logo-small {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #143028 0%, #0A1A16 100%);
  border: 1px solid rgba(218, 255, 209, 0.3);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #DAFFD1;
  font-size: 11px;
  font-weight: 900;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.moo-brand-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: -0.01em;
}

.moo-active-dot {
  width: 6px;
  height: 6px;
  background: #7be07b;
  border-radius: 50%;
  box-shadow: 0 0 8px #7be07b;
  animation: pulseDot 1.8s infinite alternate;
}

@keyframes pulseDot {
  0% {
    opacity: 0.5;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1.1);
  }
}

.moo-conv-title-subtle {
  flex: 1;
  font-size: 11.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  letter-spacing: 0.02em;
  padding: 0 4px;
}

.moo-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.moo-conv-icon-btn {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.moo-conv-icon-btn:hover {
  background: rgba(218, 255, 209, 0.08);
  color: #DAFFD1;
  border-color: rgba(218, 255, 209, 0.25);
  transform: translateY(-1px);
}

.moo-conv-icon-btn:active {
  transform: translateY(0);
}

.moo-conv-icon-btn.close-sidebar-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ff8080;
  border-color: rgba(239, 68, 68, 0.25);
}

.moo-conv-panel-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 26, 22, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: mooConvFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes mooConvFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.moo-conv-panel {
  width: 100%;
  max-width: 380px;
  max-height: 80vh;
  background: rgba(20, 48, 40, 0.75);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(218, 255, 209, 0.15);
  border-radius: 20px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 0 0 40px rgba(218, 255, 209, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: mooModalScaleUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes mooModalScaleUp {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(15px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.moo-conv-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
  background: rgba(0, 0, 0, 0.2);
}

.moo-conv-panel-title {
  font-size: 15px;
  font-weight: 700;
  color: #DAFFD1;
  letter-spacing: 0.02em;
}

.moo-conv-panel-close {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(218, 255, 209, 0.7);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;
  line-height: 1;
  padding: 0;
}

.moo-conv-panel-close:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #DAFFD1;
  border-color: rgba(218, 255, 209, 0.3);
  transform: rotate(90deg);
}

.moo-conv-new-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 16px 20px 10px;
  padding: 10px 16px;
  background: linear-gradient(135deg, rgba(218, 255, 209, 0.15) 0%, rgba(218, 255, 209, 0.05) 100%);
  border: 1px solid rgba(218, 255, 209, 0.25);
  color: #DAFFD1;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
}

.moo-conv-new-btn:hover {
  background: linear-gradient(135deg, rgba(218, 255, 209, 0.25) 0%, rgba(218, 255, 209, 0.1) 100%);
  border-color: rgba(218, 255, 209, 0.5);
  box-shadow: 0 4px 15px rgba(218, 255, 209, 0.15);
  transform: translateY(-1px);
}

.moo-conv-search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 20px 10px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(218, 255, 209, 0.1);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.moo-conv-search-box:focus-within {
  border-color: rgba(218, 255, 209, 0.35);
  background: rgba(0, 0, 0, 0.4);
  box-shadow: 0 0 12px rgba(218, 255, 209, 0.08);
}

.moo-conv-search-box svg {
  color: rgba(218, 255, 209, 0.5);
  flex-shrink: 0;
}

.moo-conv-search-box input {
  flex: 1;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 12px;
  outline: none;
  font-family: inherit;
}

.moo-conv-search-box input::placeholder {
  color: rgba(218, 255, 209, 0.35);
}

.moo-conv-search-clear {
  background: none;
  border: none;
  color: rgba(218, 255, 209, 0.5);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 4px;
}

.moo-conv-search-clear:hover {
  color: #DAFFD1;
}

.moo-conv-group-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(218, 255, 209, 0.45);
  margin-top: 14px;
  margin-bottom: 6px;
  padding-left: 4px;
  text-align: left;
}

.moo-conv-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.moo-conv-list::-webkit-scrollbar {
  width: 6px;
}

.moo-conv-list::-webkit-scrollbar-track {
  background: transparent;
}

.moo-conv-list::-webkit-scrollbar-thumb {
  background: rgba(218, 255, 209, 0.15);
  border-radius: 3px;
}

.moo-conv-list::-webkit-scrollbar-thumb:hover {
  background: rgba(218, 255, 209, 0.3);
}

.moo-conv-empty {
  padding: 24px 12px;
  font-size: 13px;
  color: rgba(218, 255, 209, 0.45);
  text-align: center;
}

.moo-conv-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-left: 3px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.moo-conv-item:hover {
  background: rgba(218, 255, 209, 0.04);
  border-color: rgba(218, 255, 209, 0.15);
  transform: translateX(2px);
}

.moo-conv-item.is-active {
  background: rgba(218, 255, 209, 0.08);
  border-color: rgba(218, 255, 209, 0.3);
  border-left: 3px solid #DAFFD1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.moo-conv-item-main {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.moo-conv-item-title {
  font-size: 13.5px;
  color: #FFFFFF;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.moo-conv-item.is-active .moo-conv-item-title {
  color: #DAFFD1;
}

.moo-conv-item-meta {
  font-size: 11px;
  color: rgba(218, 255, 209, 0.5);
  font-weight: 500;
}

.moo-conv-item-rename input {
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(218, 255, 209, 0.4);
  color: #fff;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 6px;
  outline: none;
  font-family: inherit;
}

.moo-conv-item-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

.moo-conv-item:hover .moo-conv-item-actions,
.moo-conv-item.is-active .moo-conv-item-actions {
  opacity: 1;
}

.moo-conv-item-action {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(218, 255, 209, 0.7);
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
}

.moo-conv-item-action:hover {
  background: rgba(218, 255, 209, 0.15);
  color: #DAFFD1;
  border-color: rgba(218, 255, 209, 0.4);
}

.moo-conv-item-action.ok:hover {
  color: #7be07b;
  border-color: rgba(123, 224, 123, 0.4);
  background: rgba(123, 224, 123, 0.1);
}

.moo-conv-item-action.danger:hover {
  color: #ff8080;
  border-color: rgba(255, 128, 128, 0.4);
  background: rgba(255, 128, 128, 0.1);
}
</style>
